import { QuickReplyInterface } from "@/interfaces/quickReply.interface";
import { TemplateInterface } from "@/interfaces/template.interface";
// @ts-ignore
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import axios from "@/libs/axios";
// @ts-ignore
import i18n from "@/libs/i18n";
import { computed, reactive, toRef } from "@vue/composition-api";
import { AxiosRequestConfig } from "axios";
import lodash from "lodash";
import { useToast } from "vue-toastification/composition";

interface RootState {
    quickReplies: QuickReplyInterface[];
    templates: TemplateInterface[];
    isLoading: boolean;
    searchString: string;
}

const state: RootState = reactive({
    quickReplies: [],
    templates: [],
    isLoading: true,
    searchString: "",
});

export default function useTemplates() {
    const filteredQuickReplies = computed(() => {
        const query = state.searchString.toLowerCase();
        return state.quickReplies.filter((quickReply) => {
            // @ts-ignore
            return quickReply.title.toLowerCase().includes(query);
        });
    });

    const filteredTemplates = computed(() => {
        const query = state.searchString.toLowerCase();
        return state.templates
            .filter((template) => {
                // Dont include dynamic buttons in templates
                if (template.components?.buttons?.buttons.some((button) => "dynamic" in button && button.dynamic)) {
                    return false;
                }

                return template.title.toLowerCase().includes(query);
            })
            .map((template) => ({ ...template, originalContent: template.content }));
    });

    const fetchQuickReplies = (params: AxiosRequestConfig): Promise<void> => {
        state.isLoading = true;

        return axios
            .get(`/quick-replies`, params)
            .then((response) => {
                state.quickReplies = response.data;
            })
            .catch(() => {
                const toast = useToast();
                toast({
                    component: ToastificationContent,
                    props: {
                        title: i18n.t("entities.quick-replies.Quick replies"),
                        text: i18n.t("toast.read.error"),
                        icon: "times",
                        variant: "danger",
                    },
                });
            })
            .finally(() => (state.isLoading = false));
    };

    const fetchTemplates = (requestConfig: AxiosRequestConfig, approvedOnly = true) => {
        const approvedRequestConfig: AxiosRequestConfig = approvedOnly
            ? lodash.merge(lodash.cloneDeep(requestConfig), {
                  params: {
                      s: {
                          status: "approved",
                      },
                  },
              })
            : requestConfig;

        state.isLoading = true;
        return axios
            .get(`/templates`, approvedRequestConfig)
            .then((response) => {
                state.templates = response.data;
            })
            .catch(() => {
                const toast = useToast();
                toast({
                    component: ToastificationContent,
                    props: {
                        title: i18n.t("entities.templates.approved-templates"),
                        text: i18n.t("toast.read.error"),
                        icon: "times",
                        variant: "danger",
                    },
                });
            })
            .finally(() => (state.isLoading = false));
    };

    return {
        quickReplies: filteredQuickReplies,
        templates: filteredTemplates,
        templateSearchString: toRef(state, "searchString"),
        isLoading: toRef(state, "isLoading"),
        fetchQuickReplies,
        fetchTemplates,
    };
}
